import React from 'react';
import {Users, CheckCircle, Award, Lightbulb, Heart } from 'lucide-react';
import { services } from '../data/services';
import ServiceCard from './ServiceCard';
import { motion } from 'framer-motion';

const AboutUs: React.FC = () => {

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-teal-50 py-16 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0" style={{backgroundImage: 'url(/aboutus.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative text-center">
            <motion.h1
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              About <span className="text-blue-400">HARMITON WebTech</span>
            </motion.h1>
            <motion.p
              className="text-xl text-white max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              With a deep rooted passion for technology and a mission to simplify the digital journey, HARMITON WebTech is more than just a service provider it’s your partner in building sustainable digital success.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Who <span className="text-blue-600">We Are</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                HARMITON WebTech, founded by brothers Harshal Mahadik and Mithil Mahadik, is a young and dynamic
                IT & Business Services company built on innovation, experience, and customer-centric solutions.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With a strong background of working closely with top management across diverse industries,
                we bring the expertise and passion to lead in today's digital-first world.
              </p>
            </div>
            <motion.div
              className="relative"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Team collaboration"
                className="w-full h-80 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-6 bg-white">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Our <span className="text-blue-600">Founders</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-6xl mx-auto leading-relaxed">
              Behind HARMITON WebTech is a passionate team of innovators and professionals dedicated to client success.
              Every member brings unique expertise, creativity, and commitment, ensuring that we consistently deliver
              value-driven and high-quality solutions.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, staggerChildren: 0.2 }}
            viewport={{ once: true }}
          >
            {[
              {
                name: 'Harshal Mahadik',
                position: 'CEO & Co-Founder',
                description: 'Harshal Mahadik brings 5+ years of hands-on experience in web design, development, analytics, and digital strategy. While working with a top digital marketing advisory firm in New York City, he helped brands across Entertainment, Food & Beverage, Financial Services, CPG, and Hospitality boost their online presence and drive measurable growth. With a Master’s in Information Technology Management from the University of Texas at Dallas, Harshal combines technical expertise with business insight to lead HARMITON’s mission: helping individuals and businesses thrive in the digital world.',
                image: '/Harshal.webp',
                experience: '5+ Years',
                education: 'MS in IT Management'
              },
              {
                name: 'Mithil Mahadik',
                position: 'CTO & Co-Founder',
                description: 'Mithil Mahadik is a results-oriented professional with over 4+ years of experience in software development and modern technology solutions. He holds a degree in Computer Science and Engineering, along with strong expertise in Business Analysis, Strategic Consulting, Data Analysis, and Data Visualization. Mithil has worked closely with top leadership, including the Director , VP, MD, CFO, and CEO  from leading Company, Institute and Firms. His unique blend of strategic thinking and technical proficiency has led to the successful delivery of impactful business solutions.',
                image: '/mithil.webp',
                experience: '4+ Years',
                education: 'B.Tech in Computer Science Engineering'
              }
            ].map((leader, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-8 text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <motion.div
                  className="w-60 h-70 bg-white rounded-none mx-auto mb-6 flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                >
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover rounded-none"
                  />
                </motion.div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{leader.name}</h3>
                <p className="text-blue-600 font-medium mb-4">{leader.position}</p>
                <p className="text-gray-600 leading-relaxed mb-4">{leader.description}</p>
                <div className="flex justify-center space-x-4 text-sm text-gray-500">
                  <span className="bg-blue-100 px-3 py-1 rounded-full">{leader.experience}</span>
                  <span className="bg-teal-100 px-3 py-1 rounded-full">{leader.education}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
     

    
      {/* What We Do */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What <span className="text-blue-600">We Do</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At HARMITON WebTech, we provide a wide range of tailored packages designed to help businesses
              grow and succeed in the digital world. By leveraging cutting-edge technologies, we deliver
              affordable, scalable, and results-driven solutions across multiple areas.
            </p>
          </motion.div>

          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Core Services</h3>
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, staggerChildren: 0.1 }}
              viewport={{ once: true }}
            >
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <ServiceCard service={service} />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Choose <span className="text-blue-600">Us</span>
            </h2>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, staggerChildren: 0.1 }}
            viewport={{ once: true }}
          >
            {[
              {
                icon: Users,
                title: 'Customer-First Approach',
                description: 'Solutions tailored specifically to your business goals and requirements.',
              },
              {
                icon: Award,
                title: 'Scalable & Affordable',
                description: 'Services designed for sustainable growth without breaking the budget.',
              },
              {
                icon: Lightbulb,
                title: 'Latest Technologies',
                description: 'Expertise in cutting-edge tech, blending creativity with strategic thinking.',
              },
              {
                icon: CheckCircle,
                title: 'Transparent Support',
                description: 'Reliable, result-oriented support with complete transparency throughout.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="flex items-start space-x-4">
                  <motion.div
                    className="bg-blue-100 p-3 rounded-lg flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-300 group-hover:scale-110 group-hover:rotate-6"
                    whileHover={{ scale: 1.1, rotate: 6 }}
                  >
                    <item.icon className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

 <motion.section 
        className="py-16 px-4"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            
            <h2 className="text-4xl font-bold text-slate-800 mb-6">
              Vision & <span className="text-blue-600">Mission</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              className="group"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              whileHover={{ y: -10 }}
            >
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-xl p-10 h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                    <span className="text-2xl">🔭</span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-slate-800 mb-6">Our Vision</h3>
                  <p className="text-gray-700 text-lg">
                   To make digital growth accessible, affordable, and results-driven, empowering businesses 
                   to thrive in the modern economy.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="group"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              whileHover={{ y: -10 }}
            >
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-xl p-10 h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-green-400 rounded-2xl flex items-center justify-center mb-6">
                    <span className="text-2xl">🎯</span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-slate-800 mb-6">Our Mission</h3>
                  <p className="text-gray-700 text-lg">
                    To deliver reliable IT and business services tailored to client needs, simplifying the 
                    digital journey and creating measurable impact.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Commitment to Clients */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileHover={{ y: -10 }}
          >
            <motion.div
              className="flex justify-center mb-6"
              whileHover={{ scale: 1.1 }}
            >
              <div className="bg-red-100 p-4 rounded-full">
                <Heart className="h-8 w-8 text-red-600" />
              </div>
            </motion.div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Our Commitment to <span className="text-blue-600">Clients</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              We may be new, but we are deeply committed to delivering quality, innovation, and long-term value
              for every client we partner with. Your success is at the core of everything we do and together,
              we build sustainable digital growth.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;